import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { buildVisibleMenu, isMenuItemVisible } from '@/authz/menu-visibility';
import { onLogout } from '@/constants/global';
import { pageurl } from '@/constants/pageurl';
import { useOrganizationContext } from '@/context/organization';
import { useRightPanelContext } from '@/context/right-section';
import { useRoleActionContext } from '@/context/role-actions';
import { type IMenuItem } from '@/layout/utils';
import { useLogoutMutation } from '@/store/api';

import { menuData } from '../layout/menu/components/data';

export const useAppMenu = () => {
	const [logout] = useLogoutMutation();
	const { isAllowedRoles, isLoading: isPermissionsLoading, snapshot } = useRoleActionContext();

	const organization = useOrganizationContext();

	const navigate = useNavigate();

	const [isCollapsed, setIsCollapsed] = useState<boolean>(() => localStorage.getItem('sidebar-collapsed') === 'true');

	const toggleSidebar = () => {
		setIsCollapsed((prev) => {
			localStorage.setItem('sidebar-collapsed', String(!prev));
			return !prev;
		});
	};

	// Both derive from the shared predicate in `@/authz/menu-visibility` — the command
	// palette and the route guard read the same one, so a destination can never be
	// offered somewhere it would be denied.
	const filteredMenu = useMemo(() => menuData.filter((i: IMenuItem) => isMenuItemVisible(i, snapshot)), [snapshot]);

	const groupedMenu = useMemo(() => buildVisibleMenu(menuData, snapshot), [snapshot]);

	const logoutUser = useCallback(() => {
		logout({})
			.unwrap()
			.then(() => {})
			.catch(() => {})
			.finally(() => {
				onLogout();
				window.open(pageurl.LOGIN, '_self');
			});
	}, [logout]);

	const rightPanel = useRightPanelContext();

	const electionId = organization.selectedElection.electionId;
	const hasElection = !!electionId;

	const openElectionPicker = useCallback(() => {
		if (organization.isCandidate) {
			return;
		}
		organization.onSelectElection();
	}, [organization]);

	return {
		rightPanel,
		logoutUser,
		groupedMenu,
		filteredMenu,
		toggleSidebar,
		isCollapsed,
		setIsCollapsed,
		navigate,
		organization,
		isAllowedRoles,
		snapshot,
		isPermissionsLoading,
		hasElection,
		electionId,
		openElectionPicker,
	};
};
